package com.karyadina.service

import kotlinx.serialization.json.Json
import kotlinx.serialization.encodeToString
import kotlinx.serialization.decodeFromString

/**
 * Generic key-value storage interface for persisting app data
 * Platform-specific implementations should use:
 * - Android: SharedPreferences or DataStore
 * - Web: localStorage
 * - Desktop: File-based storage
 */
interface KeyValueStorage {
    /**
     * Save a string value for a key
     */
    fun saveString(key: String, value: String)
    
    /**
     * Load a string value for a key
     */
    fun loadString(key: String): String?
    
    /**
     * Save an integer value for a key
     */
    fun saveInt(key: String, value: Int)
    
    /**
     * Load an integer value for a key
     */
    fun loadInt(key: String, defaultValue: Int = 0): Int
    
    /**
     * Save a boolean value for a key
     */
    fun saveBoolean(key: String, value: Boolean)
    
    /**
     * Load a boolean value for a key
     */
    fun loadBoolean(key: String, defaultValue: Boolean = false): Boolean
    
    /**
     * Remove a value for a key
     */
    fun remove(key: String)
    
    /**
     * Clear all stored data
     */
    fun clear()
    
    /**
     * Check if a key exists
     */
    fun contains(key: String): Boolean
    
    /**
     * Export all string data for synchronization
     */
    fun exportAll(): Map<String, String>
    
    /**
     * Import raw data map
     */
    fun importAll(data: Map<String, String>)
}

/**
 * In-memory implementation for testing and fallback
 * Note: Data will be lost when app restarts
 */
class InMemoryStorage : KeyValueStorage {
    private val storage = mutableMapOf<String, Any>()
    
    override fun saveString(key: String, value: String) {
        storage[key] = value
    }
    
    override fun loadString(key: String): String? {
        return storage[key] as? String
    }
    
    override fun saveInt(key: String, value: Int) {
        storage[key] = value
    }
    
    override fun loadInt(key: String, defaultValue: Int): Int {
        return storage[key] as? Int ?: defaultValue
    }
    
    override fun saveBoolean(key: String, value: Boolean) {
        storage[key] = value
    }
    
    override fun loadBoolean(key: String, defaultValue: Boolean): Boolean {
        return storage[key] as? Boolean ?: defaultValue
    }
    
    override fun remove(key: String) {
        storage.remove(key)
    }
    
    override fun clear() {
        storage.clear()
    }
    
    override fun contains(key: String): Boolean {
        return storage.containsKey(key)
    }
    
    override fun exportAll(): Map<String, String> {
        val result = mutableMapOf<String, String>()
        for ((k, v) in storage) {
            if (v is String) {
                result[k] = v
            }
        }
        return result
    }
    
    override fun importAll(data: Map<String, String>) {
        for ((k, v) in data) {
            storage[k] = v
        }
    }
}

/**
 * Implementation using Multiplatform Settings (SharedPreferences/NSUserDefaults)
 */
class SettingsKeyValueStorage(
    private val settings: com.russhwolf.settings.Settings
) : KeyValueStorage {
    
    override fun saveString(key: String, value: String) {
        settings.putString(key, value)
    }
    
    override fun loadString(key: String): String? {
        return if (settings.hasKey(key)) settings.getString(key, "") else null
    }
    
    override fun saveInt(key: String, value: Int) {
        settings.putInt(key, value)
    }
    
    override fun loadInt(key: String, defaultValue: Int): Int {
        return settings.getInt(key, defaultValue)
    }
    
    override fun saveBoolean(key: String, value: Boolean) {
        settings.putBoolean(key, value)
    }
    
    override fun loadBoolean(key: String, defaultValue: Boolean): Boolean {
        return settings.getBoolean(key, defaultValue)
    }
    
    override fun remove(key: String) {
        settings.remove(key)
    }
    
    override fun clear() {
        settings.clear()
    }
    
    override fun contains(key: String): Boolean {
        return settings.hasKey(key)
    }
    
    override fun exportAll(): Map<String, String> {
        val result = mutableMapOf<String, String>()
        for (key in settings.keys) {
            try {
                // Since CloudSync deals primarily with complex data stored as JSON strings,
                // we focus on exporting strings. Other types could be stringified if needed.
                val value = settings.getStringOrNull(key)
                if (value != null) {
                    result[key] = value
                }
            } catch (e: Exception) {
                // Ignore type cast errors if a key isn't a string
            }
        }
        return result
    }
    
    override fun importAll(data: Map<String, String>) {
        for ((k, v) in data) {
            settings.putString(k, v)
        }
    }
}

/**
 * Extension functions for saving/loading JSON objects
 */
inline fun <reified T> KeyValueStorage.saveJson(key: String, value: T) {
    val json = Json.encodeToString(value)
    saveString(key, json)
}

inline fun <reified T> KeyValueStorage.loadJson(key: String): T? {
    val json = loadString(key) ?: return null
    return try {
        Json.decodeFromString<T>(json)
    } catch (e: Exception) {
        null
    }
}

/**
 * Global singleton storage instance
 * Platform-specific implementations should override this
 */
object AppStorage {
    private var instance: KeyValueStorage = InMemoryStorage()

    /**
     * Flow that emits whenever importAll() is called (e.g. after cloud sync).
     * Services and ViewModels can collect this to refresh their in-memory data.
     */
    private val _refreshNotifier = kotlinx.coroutines.flow.MutableSharedFlow<Unit>(
        extraBufferCapacity = 1
    )
    val refreshNotifier: kotlinx.coroutines.flow.SharedFlow<Unit> = _refreshNotifier

    fun initialize(storage: KeyValueStorage) {
        instance = storage
    }
    
    fun get(): KeyValueStorage = instance

    /**
     * Call after importAll() to notify all observers that storage data has changed.
     */
    fun notifyRefresh() {
        _refreshNotifier.tryEmit(Unit)
    }
}

